/**
 * `/v1/notifications` and `/v1/activity` — the two endpoints the Overview page owns.
 *
 * Nothing here declares a DTO of its own: `@tvitvi/shared` is the contract and this module only
 * knows the paths and the cache tier. Every response is parsed with the very schema the API
 * serialised it with, so a contract change is a typecheck failure rather than a runtime surprise.
 *
 * The other two panels — "mine nærmeste oppgaver" and "uleste tråder" — are NOT here. They read
 * `features/tasks/queries.ts` and `features/inbox/queries.ts` as they stand, because a second
 * `GET /v1/tasks` in this directory would be a second cache key for the same rows and the first
 * one to go stale after a mutation those pages already invalidate.
 *
 * The query keys live in `apps/web/src/api/query-keys.ts` with every other key, which is where
 * CLAUDE.md says a cross-cutting concern belongs. They were briefly in this file because three
 * agents were building in parallel and two of them appending to one shared file is the merge this
 * repository organises itself to avoid; they moved back the moment nobody else was writing here.
 *
 * NEITHER FEED HAS A SORT IN ITS KEY, AND THAT IS THE CONTRACT'S DOING. Both schemas pin
 * `direction` to the literal `'desc'` and offer one sort key, because both panels are read from
 * the newest end. Ascending would start the reader at the oldest notification they ever received
 * and at the organization's first ever audit row. Two orders do not exist, so nothing addresses
 * them.
 *
 * THE UNREAD COUNT IS A SEPARATE REQUEST ON PURPOSE. `GET /v1/notifications?unread=true` returns
 * a PAGE — five rows for a panel five rows tall — and `items.length` is therefore a fact about
 * the page size, never about the backlog. `GET /v1/notifications/unread-count` is the bounded
 * aggregate that knows the difference between "5" and "at least 5", and it reports `capped` so
 * the badge can say `99+` without the client inventing the distinction. Volatile tier: it is a
 * badge, and a badge that is thirty seconds stale is a badge that is wrong.
 */
import {
  activityListResponseSchema,
  markNotificationsReadResponseSchema,
  notificationDtoSchema,
  notificationListResponseSchema,
  notificationUnreadCountSchema,
  type ActivityListResponse,
  type MarkNotificationsReadResponse,
  type NotificationDto,
  type NotificationListResponse,
  type NotificationUnreadCount,
  type ObjectType,
} from '@tvitvi/shared';
import { useEffect, useRef } from 'react';
import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { CACHE } from '@/api/cache-tiers';
import { apiRequest } from '@/api/client';
import { qk } from '@/api/query-keys';

/**
 * How many rows a panel asks for.
 *
 * A dashboard panel is a glance, not a list: five rows is what fits above the fold beside five
 * other panels, and the acceptance line for this page is "innen fem sekunder", which nobody
 * achieves by scrolling. The whole feed lives one click away on its own page — for tasks and
 * threads today, for notifications and activity when those pages exist.
 */
export const PANEL_PAGE_SIZE = 5;

/** Activity rows are one line each and carry no action, so the panel affords a few more. */
export const ACTIVITY_PANEL_PAGE_SIZE = 8;

/**
 * The panel's feed: the caller's own unread notifications, newest first.
 *
 * There is no `recipientPersonId` to send and none comes back. `r_notifications_recipient` is a
 * RESTRICTIVE policy on `recipient_person_id = app.require_person_id()` with no admin override,
 * so every row this endpoint can return is already addressed to the caller — which is also why
 * this cache entry is never shared between two people and never survives an organization switch.
 */
export function unreadNotificationsQueryOptions(orgId: string, limit = PANEL_PAGE_SIZE) {
  return queryOptions({
    /*
     * `limit` STÅR I NØKKELEN (QA5-B). Klokka ber om åtte, oversikten fem og varselfanen femti,
     * men de delte én cache-oppføring — den som hentet først bestemte hvor mange alle tre viste.
     * Klokka sto med fem, og varselfanen kunne gjøre det samme.
     */
    queryKey: [...qk.notificationList(orgId, true), limit] as const,
    queryFn: ({ signal }): Promise<NotificationListResponse> =>
      apiRequest({
        path: '/notifications',
        /*
         * NØKKELENS ORGANISASJON ER HEADERENS (P10, funn C). Alle varselkallene sender den de er
         * nøkkelet på; i gjestemodus er det personens egen (`homeOrganizationIdFor`), ikke den
         * headeren ellers ville navngitt og der svaret var 403.
         */
        organizationId: orgId,
        query: { sort: 'recent', direction: 'desc', limit, unread: true },
        schema: notificationListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/** The badge. `count` is capped at `UNREAD_BADGE_MAX`; `capped` says whether the cap was hit. */
export function unreadCountQueryOptions(orgId: string) {
  return queryOptions({
    queryKey: qk.notificationUnreadCount(orgId),
    queryFn: ({ signal }): Promise<NotificationUnreadCount> =>
      apiRequest({
        path: '/notifications/unread-count',
        organizationId: orgId,
        schema: notificationUnreadCountSchema,
        signal,
      }),
    ...CACHE.volatile,
  });
}

/**
 * "Siste aktivitet" — the organization's change log, newest first.
 *
 * `audit_log:list` is `yes, yes, no, no, no`, so this request is only ever made by a superadmin
 * or an admin. The panel asks `can()` before it mounts (decision 0005's rule applied to a panel;
 * contract C-44) and the API refuses again on every call, which is principle 7's two halves.
 */
/**
 * One object's own change log — what `PAGES.md` calls the endringslogg (sak 49).
 *
 * The same endpoint and the same cell as the feed above, narrowed by `entityType` + `entityId`.
 * The contract validates the pair as a pair, so both are required here too: an id compared across
 * every table in the organisation is a query no index can serve.
 *
 * IT LIVES BESIDE THE FEED RATHER THAN IN `features/contacts`, because it is the same request with
 * a different question — and a second copy of the path, the schema and the cache tier is the first
 * one to go stale when any of the three changes.
 */
export function entityActivityQueryOptions(
  orgId: string,
  entityType: string,
  entityId: string,
  limit = ACTIVITY_PANEL_PAGE_SIZE,
) {
  return queryOptions({
    queryKey: qk.activityFor(orgId, entityType, entityId, limit),
    queryFn: ({ signal }): Promise<ActivityListResponse> =>
      apiRequest({
        path: '/activity',
        query: { sort: 'recent', direction: 'desc', limit, entityType, entityId },
        schema: activityListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

export function activityQueryOptions(orgId: string, limit = ACTIVITY_PANEL_PAGE_SIZE) {
  return queryOptions({
    queryKey: qk.activityList(orgId, limit),
    queryFn: ({ signal }): Promise<ActivityListResponse> =>
      apiRequest({
        path: '/activity',
        query: { sort: 'recent', direction: 'desc', limit },
        schema: activityListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * «Siste aktivitet fra endringsloggen» for ONE object — the årgang's Overview asks it about the
 * edition row.
 *
 * WHAT IT IS NOT, said plainly: this is the log of the YEAR'S OWN ROW — its name, its dates, its
 * status — and not of everything inside it. `audit_log` records which table and which id a change
 * touched, and a task belonging to an edition is a fact about the TASK's columns rather than
 * anything the log indexes. Widening it means putting `edition_id` on `audit_log` and deciding
 * what «in the edition» covers, which is a product decision (sak 62) rather than a query.
 */
export function objectActivityQueryOptions(
  orgId: string,
  entityType: string,
  entityId: string,
  limit = ACTIVITY_PANEL_PAGE_SIZE,
) {
  return queryOptions({
    queryKey: qk.objectActivityList(orgId, entityType, entityId, limit),
    queryFn: ({ signal }): Promise<ActivityListResponse> =>
      apiRequest({
        path: '/activity',
        query: { sort: 'recent', direction: 'desc', limit, entityType, entityId },
        schema: activityListResponseSchema,
        signal,
      }),
    ...CACHE.list,
  });
}

/**
 * One notification read.
 *
 * The endpoint answers with the row as it now stands, so the panel drops it from the unread list
 * and decrements the badge off the SERVER's answer rather than a guess — and the declared
 * invalidation refetches behind that, which picks up anything that arrived meanwhile.
 */
export function useMarkNotificationRead(
  orgId: string,
): UseMutationResult<NotificationDto, unknown, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (notificationId: string) =>
      apiRequest({
        path: `/notifications/${notificationId}/read`,
        organizationId: orgId,
        method: 'POST',
        schema: notificationDtoSchema,
      }),
    onSuccess: (notification) => {
      dropFromUnread(queryClient, orgId, (row) => row.id !== notification.id);
      adjustUnreadCount(queryClient, orgId, -1);
    },
    meta: { invalidates: [qk.notifications(orgId)] },
  });
}

/**
 * "Les alt", in one request rather than one per row — the N+1 principle 6 rules out.
 *
 * The response is a count, not a page, so there is nothing to reconcile row by row: the caller
 * asked for the unread feed to be empty, and after a success it is. Writing that emptiness into
 * the cache is what makes the panel clear on the click instead of after a round trip.
 */
export function useMarkAllNotificationsRead(
  orgId: string,
): UseMutationResult<MarkNotificationsReadResponse, unknown, void> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiRequest({
        path: '/notifications/read-all',
        organizationId: orgId,
        method: 'POST',
        schema: markNotificationsReadResponseSchema,
      }),
    onSuccess: () => {
      dropFromUnread(queryClient, orgId, () => false);
      queryClient.setQueryData<NotificationUnreadCount>(qk.notificationUnreadCount(orgId), {
        count: 0,
        capped: false,
      });
    },
    meta: { invalidates: [qk.notifications(orgId)] },
  });
}

/**
 * "I opened it, so stop telling me about it" (decision 0012, Atle's question 2).
 *
 * ---------------------------------------------------------------------------------------------
 * WHY THIS IS A CLIENT EFFECT AND NOT SOMETHING THE GET DOES
 *
 * Clearing the notifications inside `GET /tasks/:id` would be one line on the server and wrong:
 * a GET that writes is not cacheable and not safely retryable, and this app PREFETCHES on hover,
 * so a pointer crossing a task list would silently empty somebody's feed. Whether a person
 * actually READ something is a fact only the client holds — the detail view was mounted, not
 * merely fetched — so the client is what says so.
 *
 * IT FIRES ONCE PER OBJECT, NOT ONCE PER RENDER. The effect depends on the pair, and the ref
 * remembers what it has already cleared: React 18's development double-mount, a re-render from a
 * refetch, and a parent that re-creates its props all produce a second run, and each would be a
 * pointless POST. Navigating to a different object clears the ref by changing the key.
 *
 * IT IS FIRE-AND-FORGET, AND A FAILURE IS SILENT ON PURPOSE. Nothing the reader asked for has
 * failed if this request does — they opened a task and the task is there. Surfacing an error for
 * a bookkeeping write nobody requested would be noise about something they cannot act on, and the
 * notification simply stays unread, which is the safe direction.
 */
export function useClearNotificationsForObject(
  orgId: string,
  objectType: ObjectType,
  objectId: string | undefined,
): void {
  useClearNotificationsForObjects(orgId, objectType, objectId === undefined ? NONE : [objectId]);
}

const NONE: readonly string[] = [];

/**
 * DET SAMME FOR FLERE TING PÅ ÉN GANG (QA-runde 13, punkt 111): en skuff som viser en artists bud
 * hos festivalen har varsler på hvert av dem. Hver id ryddes én gang per skuff, som over.
 *
 * Skuffene som viser en sak kaller denne når de åpnes — tråden (`ThreadDetailPage`), byråets
 * forespørsel (`EngagementDialog`) og festivalens artistkort med budene (`ArtistDialog`) — så
 * varselet merkes lest uansett veien inn, ikke bare når det ble trykket på.
 */
export function useClearNotificationsForObjects(
  orgId: string,
  objectType: ObjectType,
  objectIds: readonly string[],
): void {
  const queryClient = useQueryClient();
  const cleared = useRef<Set<string>>(new Set());
  /* Avhengigheten er innholdet, ikke arrayens identitet: en ny liste med de samme id-ene er ingen ny skuff. */
  const key = objectIds.join(',');

  useEffect(() => {
    if (key === '' || orgId === '') return;
    for (const objectId of key.split(',')) {
      const seen = `${objectType}:${objectId}`;
      if (cleared.current.has(seen)) continue;
      cleared.current.add(seen);

      clearObjectNotifications(queryClient, orgId, objectType, objectId);
    }
  }, [queryClient, orgId, objectType, key]);
}

/**
 * ÉN TING MERKES LEST — kroppen hooken over og ankomstvakten (`useClearNotificationsOnArrival`)
 * deler. Ild-og-glem, og en feil er stille med vilje (se over): varselet står da ulest.
 */
export function clearObjectNotifications(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  objectType: ObjectType,
  objectId: string,
): void {
  /* Bak `Promise.resolve()`: også en feil som kastes før løftet finnes, er stille (se over). */
  void Promise.resolve()
    .then(() =>
      apiRequest({
        path: '/notifications/read-by-object',
        organizationId: orgId,
        method: 'POST',
        body: { objectType, objectId },
        schema: markNotificationsReadResponseSchema,
      }),
    )
    .then(({ markedCount }) => {
      // Nothing pointed here, so nothing in the cache is stale — and the badge must not move.
      if (markedCount === 0) return;
      dropFromUnread(
        queryClient,
        orgId,
        (row) => row.objectType !== objectType || row.objectId !== objectId,
      );
      adjustUnreadCount(queryClient, orgId, -markedCount);
      void queryClient.invalidateQueries({ queryKey: qk.notifications(orgId) });
    })
    .catch(() => {
      // Deliberately swallowed; see the note above. The notification stays unread.
    });
}

/** Keeps only the rows `keep` accepts, in every cached unread page (one per `limit`). */
function dropFromUnread(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  keep: (row: NotificationDto) => boolean,
): void {
  queryClient.setQueriesData<NotificationListResponse>(
    { queryKey: qk.notificationList(orgId, true) },
    (page) => (page === undefined ? page : { ...page, items: page.items.filter(keep) }),
  );
}

/**
 * Moves the badge by `delta`, floored at zero.
 *
 * `capped` is deliberately left alone when it was true: the count is `UNREAD_BADGE_MAX` because
 * the aggregate stopped counting there, so ninety-nine minus one is not ninety-eight — it is
 * still "at least ninety-eight", and only the refetch behind the invalidation knows the real
 * number. Rendering `98` there would be the one thing the `capped` flag exists to prevent.
 */
function adjustUnreadCount(
  queryClient: ReturnType<typeof useQueryClient>,
  orgId: string,
  delta: number,
): void {
  const key = qk.notificationUnreadCount(orgId);
  const current = queryClient.getQueryData<NotificationUnreadCount>(key);
  if (!current || current.capped) return;
  queryClient.setQueryData<NotificationUnreadCount>(key, {
    count: Math.max(current.count + delta, 0),
    capped: current.capped,
  });
}
